// --------------------------------------------------------------
// Created On: 2024-09-14
// Author: Zachary Thomas
//
// Last Modified: 2025-10-27
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useMemo } from "react";
import { Active, Over, useDraggable } from "@dnd-kit/core";
import {
  HISTORY_GRAPH_CARD_TYPE,
  CARD_TYPES_THAT_SUPPORT_ATTRIBUTES,
  MAX_CARD_ATTRIBUTES,
  MAX_GRAPH_CARD_ATTRIBUTES,
} from "../../../../../constants/templates";
import { DndContext } from "@dnd-kit/core";
import DroppableArea from "../../../../../components/DroppableArea/DroppableArea";
import TemplateAttribute from "../../TemplateAttribute/TemplateAttribute";
import deepCopy from "../../../../../utilities/deepCopy";
import templateGraphIsAccumulative from "src/utilities/templateGraphIsAccumulative";
import SpecialCardTooltips from "./SpecialCardTooltips/SpecialCardTooltips";
import AddAttributeButton from "./AddAttributeButton/AddAttributeButton";
import styles from "./TemplateCard.module.scss";

// Form for a single template card.
export default function TemplateCard(props: Props): Component {
  const selectedCardType = useMemo(
    () => getCardType(props.cardTypeId, props.templateCardTypes),
    [props.cardTypeId, JSON.stringify(props.templateCardTypes)]
  );
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: props.formId,
  });
  const cardHasNameField =
    props.cardTypeId === null || (selectedCardType !== null && selectedCardType.name === HISTORY_GRAPH_CARD_TYPE);
  const cardSupportsAttributes =
    props.cardTypeId === null ||
    (selectedCardType !== null && CARD_TYPES_THAT_SUPPORT_ATTRIBUTES.includes(selectedCardType.name));

  // Gets the current selected card type given a card type ID.
  function getCardType(
    cardTypeId: string | number | null,
    templateCardTypes: TemplateCardType[]
  ): TemplateCardType | null {
    if (cardTypeId === null) {
      return null;
    } else {
      const selectedCardType = templateCardTypes.find(
        (templateCardType) => templateCardType.templateCardTypeId === parseInt(`${cardTypeId}`, 10)
      );
      if (selectedCardType === undefined) {
        return null;
      } else {
        return selectedCardType;
      }
    }
  }

  // Handle changes to the current card.
  function handleCardChange(
    name: string,
    cardTypeId: string | number | null,
    attributes: Attribute[],
    historyGraphTypeId: number | null,
    historyGraphAxisTypeId: number | null,
    isExtraWide: boolean | null,
    numberOfDaysShown: number | string | null
  ): void {
    let parsedCardTypeId: number | null = parseInt(String(cardTypeId), 10);
    if (parsedCardTypeId === undefined || isNaN(parsedCardTypeId)) {
      parsedCardTypeId = null;
    }
    // Construct a graph object is all required fields are present.
    let graph: Graph | null = null;
    if (
      historyGraphTypeId !== null &&
      historyGraphAxisTypeId !== null &&
      isExtraWide !== null &&
      numberOfDaysShown !== null
    ) {
      graph = {
        historyGraphTypeId: historyGraphTypeId,
        historyGraphAxisTypeId: historyGraphAxisTypeId,
        isExtraWide: isExtraWide,
        numberOfDaysShown: numberOfDaysShown,
      };
    }
    // If the card type is historical graph, and we don't have a graph object yet, then generate an initial graph object.
    const cardType = getCardType(cardTypeId, props.templateCardTypes);
    if (cardType !== null && cardType.name === HISTORY_GRAPH_CARD_TYPE && graph === null) {
      graph = {
        historyGraphTypeId: 0,
        historyGraphAxisTypeId: 0,
        isExtraWide: false,
        numberOfDaysShown: 7,
      };
    }
    props.onChange(name, parsedCardTypeId, attributes, graph);
  }

  // Handles adding an attribute to the current card.
  function handleAddAttribute(
    name: string,
    cardTypeId: string | number | null,
    attributes: Attribute[],
    graph: Graph | null
  ): void {
    let attributesDeepCopy = deepCopy(attributes);
    let newAttributeFormId = 1;
    attributesDeepCopy.forEach((attribute) => {
      if (attribute.formId >= newAttributeFormId) {
        newAttributeFormId = attribute.formId + 1;
      }
    });
    const newAttribute = {
      formId: newAttributeFormId,
      regAttributeId: 0,
      attributeName: "",
      unitId: null,
      unitShortName: null,
      unitLongName: null,
      isAccumulative: false,
      isGraphable: false,
    };
    attributesDeepCopy = [...attributesDeepCopy, newAttribute];
    handleCardChange(
      name,
      cardTypeId,
      attributesDeepCopy,
      graph === null ? null : graph.historyGraphTypeId,
      graph === null ? null : graph.historyGraphAxisTypeId,
      graph === null ? null : graph.isExtraWide,
      graph === null ? null : graph.numberOfDaysShown
    );
  }

  // Handles updating an attribute on the current card.
  function handleUpdateAttribute(
    formId: number,
    attributeName: string,
    cardName: string,
    cardTypeId: string | number | null,
    attributes: Attribute[],
    attributeMap: AttributesById,
    graph: Graph | null
  ): void {
    const attributesDeepCopy = deepCopy(attributes);
    const selectedAttribute = attributesDeepCopy.find((attribute) => attribute.formId === formId);
    if (selectedAttribute !== undefined) {
      const attributes = Object.keys(attributeMap).map((key) => attributeMap[key]);
      const lookUpAttribute = attributes.find((attribute) => attribute.attributeName === attributeName);
      if (lookUpAttribute !== undefined) {
        selectedAttribute.regAttributeId = lookUpAttribute.regAttributeId;
        selectedAttribute.attributeName = lookUpAttribute.attributeName;
        selectedAttribute.unitId = lookUpAttribute.unitId;
        selectedAttribute.unitShortName = lookUpAttribute.unitShortName;
        selectedAttribute.unitLongName = lookUpAttribute.unitLongName;
        selectedAttribute.isAccumulative = lookUpAttribute.isAccumulative;
        selectedAttribute.isGraphable = lookUpAttribute.isGraphable;
      } else {
        selectedAttribute.regAttributeId = 0;
      }
      selectedAttribute.attributeName = attributeName;
      handleCardChange(
        cardName,
        cardTypeId,
        attributesDeepCopy,
        graph === null ? null : graph.historyGraphTypeId,
        graph === null ? null : graph.historyGraphAxisTypeId,
        graph === null ? null : graph.isExtraWide,
        graph === null ? null : graph.numberOfDaysShown
      );
    }
  }

  // Handles deleting an attribute on the current card.
  function handleDeleteAttribute(
    formId: number,
    cardName: string,
    cardTypeId: string | number | null,
    attributes: Attribute[],
    graph: Graph | null
  ): void {
    const attributesDeepCopy = deepCopy(attributes);
    const selectedAttributeIndex = attributesDeepCopy.findIndex((attribute) => attribute.formId === formId);
    if (selectedAttributeIndex >= 0) {
      attributesDeepCopy.splice(selectedAttributeIndex, 1);
      handleCardChange(
        cardName,
        cardTypeId,
        attributesDeepCopy,
        graph === null ? null : graph.historyGraphTypeId,
        graph === null ? null : graph.historyGraphAxisTypeId,
        graph === null ? null : graph.isExtraWide,
        graph === null ? null : graph.numberOfDaysShown
      );
    }
  }

  // Handles changing the order of cards when dragged to a new position.
  function handleAttributeDragEnd(
    over: Over | null,
    active: Active,
    cardName: string,
    cardTypeId: string | number | null,
    attributes: Attribute[],
    graph: Graph | null
  ): void {
    if (over !== null) {
      let attributesDeepCopy = deepCopy(attributes);
      const selectedAttributeIndex = attributesDeepCopy.findIndex((attribute) => attribute.formId === active.id);
      const selectedAttribute = attributesDeepCopy.find((attribute) => attribute.formId === active.id);
      if (selectedAttributeIndex > -1 && selectedAttribute !== undefined) {
        // Start by removing the attribute from the array before adding it to its new location.
        attributesDeepCopy.splice(selectedAttributeIndex, 1);
        // Figure out where the attribute gets added back into the array.
        if (over.id === 0) {
          attributesDeepCopy = [selectedAttribute, ...attributesDeepCopy];
          handleCardChange(
            cardName,
            cardTypeId,
            attributesDeepCopy,
            graph === null ? null : graph.historyGraphTypeId,
            graph === null ? null : graph.historyGraphAxisTypeId,
            graph === null ? null : graph.isExtraWide,
            graph === null ? null : graph.numberOfDaysShown
          );
        } else {
          const previousAttributeIndex = attributesDeepCopy.findIndex((attribute) => attribute.formId === over.id);
          if (previousAttributeIndex > -1) {
            attributesDeepCopy.splice(previousAttributeIndex + 1, 0, selectedAttribute);
            handleCardChange(
              cardName,
              cardTypeId,
              attributesDeepCopy,
              graph === null ? null : graph.historyGraphTypeId,
              graph === null ? null : graph.historyGraphAxisTypeId,
              graph === null ? null : graph.isExtraWide,
              graph === null ? null : graph.numberOfDaysShown
            );
          }
        }
      }
    }
  }

  return (
    <div className="row align-items-center">
      <div className="col-auto ge-0 pe-0">{props.formIndex + 1}.</div>
      <div className="col g-0">
        <div
          data-test="card-attribute-container"
          className="container m-0 py-0"
          ref={setNodeRef}
          style={
            transform
              ? {
                  transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
                }
              : undefined
          }
        >
          <div className={`${styles.body} row p-0 m-0`}>
            <div className={`${styles.draggable} col-6 col-lg-auto p-0 m-0`} {...attributes} {...listeners} />
            <div className="col py-2 pe-2">
              <div className="row align-items-center my-2 my-lg-1">
                {/* Drop down for selecting card type. */}
                <div className="col-auto my-2 my-lg-1">
                  <label className={styles.title}>Card Type</label>
                </div>
                <div className="col-8 col-lg my-2 my-lg-1">
                  <select
                    data-test="card-type-selection"
                    className="form-select"
                    value={props.cardTypeId || ""}
                    disabled={props.disabled}
                    onChange={(e) =>
                      handleCardChange(
                        props.name,
                        e.target.value,
                        props.attributes,
                        props.graph === null ? null : props.graph.historyGraphTypeId,
                        props.graph === null ? null : props.graph.historyGraphAxisTypeId,
                        props.graph === null ? null : props.graph.isExtraWide,
                        props.graph === null ? null : props.graph.numberOfDaysShown
                      )
                    }
                  >
                    <option value="">Custom Card</option>
                    {props.templateCardTypes.map((templateCardType) => (
                      <option key={templateCardType.templateCardTypeId} value={templateCardType.templateCardTypeId}>
                        {templateCardType.name}
                      </option>
                    ))}
                  </select>
                </div>

                {props.cardTypeId !== null && (
                  <SpecialCardTooltips
                    cardTypeId={props.cardTypeId}
                    cardTypeName={selectedCardType === null ? null : selectedCardType.name}
                    cardTypeDescription={selectedCardType === null ? null : selectedCardType.description}
                  />
                )}

                {cardHasNameField && (
                  <Fragment>
                    <div className="col-auto my-2 my-lg-1">
                      <label className={styles.title}>Name</label>
                    </div>
                    <div className="col-5 col-lg my-2 my-lg-1">
                      <input
                        data-test="card-name-input"
                        className="form-control mx-auto"
                        type="text"
                        value={props.name}
                        disabled={props.disabled}
                        onChange={(e) =>
                          handleCardChange(
                            e.target.value,
                            props.cardTypeId,
                            props.attributes,
                            props.graph === null ? null : props.graph.historyGraphTypeId,
                            props.graph === null ? null : props.graph.historyGraphAxisTypeId,
                            props.graph === null ? null : props.graph.isExtraWide,
                            props.graph === null ? null : props.graph.numberOfDaysShown
                          )
                        }
                      />
                    </div>
                  </Fragment>
                )}

                {props.cardTypeId === null && (
                  <div className="col-auto my-2 my-lg-1">
                    <AddAttributeButton
                      maxAttributes={MAX_CARD_ATTRIBUTES}
                      currentAttributeCount={props.attributes.length}
                      onAddAttribute={() =>
                        handleAddAttribute(props.name, props.cardTypeId, props.attributes, props.graph)
                      }
                      disabled={props.disabled}
                    />
                  </div>
                )}

                {/* Button for deleting this card. */}
                <div className="col-auto my-2 my-lg-1">
                  <button
                    data-test="delete-card-button"
                    type="button"
                    className={`${styles.button} btn btn-danger float-end`}
                    disabled={props.disabled}
                    onClick={() => props.onDelete()}
                  >
                    <i className="d-inline fa fa-fw fa-times fa-xs" />
                  </button>
                </div>
              </div>

              {/* Controls for history graph cards. */}
              {selectedCardType !== null && selectedCardType.name === HISTORY_GRAPH_CARD_TYPE && (
                <div className="row align-items-center my-2">
                  <div className="col-12 col-md-6 my-2">
                    <div className="row align-items-center">
                      <div className="col-3">
                        <label className={styles.title}>Graph Mode</label>
                      </div>
                      <div className="col-9">
                        <select
                          data-test="graph-mode-selection"
                          className="form-select"
                          value={props.graph === null ? "" : props.graph.historyGraphTypeId}
                          disabled={props.disabled}
                          onChange={(e) =>
                            handleCardChange(
                              props.name,
                              props.cardTypeId,
                              props.attributes,
                              parseInt(e.target.value, 10),
                              props.graph === null ? null : props.graph.historyGraphAxisTypeId,
                              props.graph === null ? null : props.graph.isExtraWide,
                              props.graph === null ? null : props.graph.numberOfDaysShown
                            )
                          }
                        >
                          <option value="0" disabled>
                            Please select a graphing mode...
                          </option>
                          {props.historyGraphTypes.map((historyGraphType) => (
                            <option
                              key={historyGraphType.historyGraphTypeId}
                              value={historyGraphType.historyGraphTypeId}
                            >
                              {historyGraphType.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-6 my-2">
                    <div className="row align-items-center">
                      <div className="col-3">
                        <label className={styles.title}>Axis Labels</label>
                      </div>
                      <div className="col-9">
                        <select
                          data-test="axis-label-selection"
                          className="form-select"
                          value={props.graph === null ? "" : props.graph.historyGraphAxisTypeId}
                          disabled={props.disabled}
                          onChange={(e) =>
                            handleCardChange(
                              props.name,
                              props.cardTypeId,
                              props.attributes,
                              props.graph === null ? null : props.graph.historyGraphTypeId,
                              parseInt(e.target.value, 10),
                              props.graph === null ? null : props.graph.isExtraWide,
                              props.graph === null ? null : props.graph.numberOfDaysShown
                            )
                          }
                        >
                          <option value="0" disabled>
                            Please select an axis label setting...
                          </option>
                          {props.historyGraphAxisTypes.map((historyGraphAxisType) => (
                            <option
                              key={historyGraphAxisType.historyGraphAxisTypeId}
                              value={historyGraphAxisType.historyGraphAxisTypeId}
                            >
                              {historyGraphAxisType.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-6 my-2">
                    <div className="row align-items-center">
                      <div className="col-3">
                        <label className={styles.title}>Days Shown</label>
                      </div>
                      <div className="col-9">
                        <input
                          data-test="number-of-days-shown-input"
                          className="form-control mx-auto"
                          type="text"
                          value={props.graph === null ? "" : props.graph.numberOfDaysShown}
                          disabled={props.disabled}
                          onChange={(e) =>
                            handleCardChange(
                              props.name,
                              props.cardTypeId,
                              props.attributes,
                              props.graph === null ? null : props.graph.historyGraphTypeId,
                              props.graph === null ? null : props.graph.historyGraphAxisTypeId,
                              props.graph === null ? null : props.graph.isExtraWide,
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-md-6 my-2">
                    <div className="row align-items-center">
                      <div className="col-3">
                        <label className={styles.title}>Full Width</label>
                      </div>
                      <div className="col-9">
                        <input
                          data-test="full-width-checkbox"
                          className="form-check-input form-check-big"
                          type="checkbox"
                          checked={props.graph === null ? false : props.graph.isExtraWide}
                          disabled={props.disabled}
                          onChange={(e) =>
                            handleCardChange(
                              props.name,
                              props.cardTypeId,
                              props.attributes,
                              props.graph === null ? null : props.graph.historyGraphTypeId,
                              props.graph === null ? null : props.graph.historyGraphAxisTypeId,
                              e.target.checked,
                              props.graph === null ? null : props.graph.numberOfDaysShown
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedCardType !== null && selectedCardType.name === HISTORY_GRAPH_CARD_TYPE && (
                <div className="row align-items-center my-2">
                  <div className="col">
                    <AddAttributeButton
                      maxAttributes={MAX_GRAPH_CARD_ATTRIBUTES}
                      currentAttributeCount={props.attributes.length}
                      onAddAttribute={() =>
                        handleAddAttribute(props.name, props.cardTypeId, props.attributes, props.graph)
                      }
                      disabled={props.disabled}
                    />
                  </div>
                </div>
              )}

              {cardSupportsAttributes && props.attributes.length > 0 && (
                <div className="row align-items-center my-2">
                  <div className="col">
                    <DndContext
                      onDragEnd={(e) =>
                        handleAttributeDragEnd(
                          e.over,
                          e.active,
                          props.name,
                          props.cardTypeId,
                          props.attributes,
                          props.graph
                        )
                      }
                    >
                      <DroppableArea id={0} disabled={props.disabled} isSlim />
                      {props.attributes.map((attribute, i) => (
                        <Fragment key={attribute.formId}>
                          <TemplateAttribute
                            key={attribute.formId}
                            formIndex={i}
                            formId={attribute.formId}
                            name={attribute.attributeName}
                            showOnlyGraphable={
                              selectedCardType !== null && selectedCardType.name === HISTORY_GRAPH_CARD_TYPE
                            }
                            showOnlyAccumulative={
                              selectedCardType !== null &&
                              selectedCardType.name === HISTORY_GRAPH_CARD_TYPE &&
                              templateGraphIsAccumulative(props.graph, props.historyGraphTypes)
                            }
                            disabled={props.disabled}
                            onChange={(name) => {
                              handleUpdateAttribute(
                                attribute.formId,
                                name,
                                props.name,
                                props.cardTypeId,
                                props.attributes,
                                props.attributeMap,
                                props.graph
                              );
                            }}
                            onDelete={() =>
                              handleDeleteAttribute(
                                attribute.formId,
                                props.name,
                                props.cardTypeId,
                                props.attributes,
                                props.graph
                              )
                            }
                            onDragEnd={(over, active) =>
                              handleAttributeDragEnd(
                                over,
                                active,
                                props.name,
                                props.cardTypeId,
                                props.attributes,
                                props.graph
                              )
                            }
                          />
                          <DroppableArea id={attribute.formId} disabled={props.disabled} isSlim />
                        </Fragment>
                      ))}
                    </DndContext>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  formIndex: number;
  formId: number;
  name: string;
  cardTypeId: number | null;
  graph: Graph | null;
  attributeMap: AttributesById;
  attributes: Attribute[];
  templateCardTypes: TemplateCardType[];
  historyGraphAxisTypes: HistoryGraphAxisType[];
  historyGraphTypes: HistoryGraphType[];
  disabled?: boolean;
  onChange: (name: string, cardTypeId: number | null, attributes: Attribute[], graph: Graph | null) => void;
  onDelete: () => void;
  onDragEnd: (over: Over | null, active: Active) => void;
}

interface Graph {
  historyGraphTypeId: number;
  historyGraphAxisTypeId: number;
  isExtraWide: boolean;
  numberOfDaysShown: number | string;
}

interface TemplateCardType {
  templateCardTypeId: number;
  name: string;
  description: string;
}

interface HistoryGraphAxisType {
  historyGraphAxisTypeId: number;
  name: string;
  description: string;
}

interface HistoryGraphType {
  historyGraphTypeId: number;
  name: string;
  description: string;
}

interface Attribute {
  formId: number;
  regAttributeId: number;
  attributeName: string;
  unitId: number | null;
  unitShortName: string | null;
  unitLongName: string | null;
  isAccumulative: boolean;
  isGraphable: boolean;
}

interface AttributesById {
  [key: string]: Attribute;
}
